/**
 * Accessible lightbox for the image carousel content element.
 *
 * - Uses a native <dialog> (with role="dialog" fallback), aria-modal, labelled dialog
 * - Focus is trapped inside the lightbox and restored to the carousel on close
 * - Keyboard: Escape closes, ArrowLeft / ArrowRight navigate, Tab cycles inside
 * - Live region announces the current image for screen reader users
 * - Prev / next arrows cycle through all images of the carousel
 */
export default class ImageCarouselLightbox {

  defaultOptions = {
    triggerSelector: '.image-carousel__zoom',
    imageItemSelector: '.image-carousel__image',
    clonedSlideSelector: '.slick-cloned',
    openHtmlClass: 'image-lightbox-open',
    onClose: null,
    labels: {
      title: 'Bildansicht',
      close: 'Schließen',
      prev: 'Vorheriges Bild',
      next: 'Nächstes Bild',
      counter: 'Bild %current% von %total%'
    }
  };

  constructor(carouselElement, options = {}) {
    this.carousel = carouselElement;
    this.options = Object.assign({}, this.defaultOptions, options);
    this.options.labels = Object.assign({}, this.defaultOptions.labels, options.labels || {});

    this.triggers = Array.from(this.carousel.querySelectorAll(this.options.triggerSelector))
        .filter((trigger) => !trigger.closest(this.options.clonedSlideSelector));

    if (this.triggers.length < 1) {
      return;
    }

    this.items = this.triggers.map((trigger) => ({
      src: trigger.dataset.lightboxSrc || '',
      srcset: trigger.dataset.lightboxSrcset || '',
      alt: trigger.dataset.lightboxAlt || '',
      caption: trigger.dataset.lightboxCaption || ''
    }));

    this.currentIndex = 0;
    this.isOpen = false;
    this.lastFocused = null;
    this.dialog = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);

    this.bindTriggers();
  }

  /* ------------------------------------------------------------------ */
  /* Triggers                                                            */
  /* ------------------------------------------------------------------ */

  bindTriggers() {
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(index, trigger);
      });

      // The trigger button itself is visually hidden (keyboard / screen reader
      // only). Mouse and touch users open the lightbox by clicking or tapping
      // the whole image. Only react to real clicks / taps, not to the end of a
      // slick swipe gesture.
      const item = trigger.closest(this.options.imageItemSelector);
      const image = item ? item.querySelector('img') : null;
      if (!image) {
        return;
      }

      let pointerStart = null;
      image.addEventListener('pointerdown', (event) => {
        pointerStart = {x: event.clientX, y: event.clientY};
      });
      image.addEventListener('click', (event) => {
        if (pointerStart) {
          const moved = Math.abs(event.clientX - pointerStart.x) + Math.abs(event.clientY - pointerStart.y);
          pointerStart = null;
          if (moved > 10) {
            return;
          }
        }
        event.preventDefault();
        this.open(index, trigger);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Dialog markup                                                       */
  /* ------------------------------------------------------------------ */

  supportsDialog() {
    return typeof HTMLDialogElement === 'function'
        && typeof HTMLDialogElement.prototype.showModal === 'function';
  }

  buildDialog() {
    const labels = this.options.labels;
    const useNativeDialog = this.supportsDialog();

    const dialog = document.createElement(useNativeDialog ? 'dialog' : 'div');
    dialog.className = 'image-lightbox';
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', labels.title);

    if (!useNativeDialog) {
      dialog.setAttribute('role', 'dialog');
      dialog.hidden = true;
    }

    dialog.innerHTML = `
      <div class="image-lightbox__inside">
        <p class="image-lightbox__counter" aria-hidden="true"></p>
        <p class="image-lightbox__status sr-only" aria-live="polite" aria-atomic="true"></p>

        <button type="button" class="image-lightbox__close" aria-label="${labels.close}"></button>

        <button type="button" class="image-lightbox__arrow image-lightbox__arrow--prev" aria-label="${labels.prev}"></button>

        <figure class="image-lightbox__figure">
          <img class="image-lightbox__image" src="" alt="" decoding="async">
          <figcaption class="image-lightbox__caption"></figcaption>
        </figure>

        <button type="button" class="image-lightbox__arrow image-lightbox__arrow--next" aria-label="${labels.next}"></button>
      </div>
    `;

    this.dialog = dialog;
    this.elements = {
      inside: dialog.querySelector('.image-lightbox__inside'),
      counter: dialog.querySelector('.image-lightbox__counter'),
      status: dialog.querySelector('.image-lightbox__status'),
      close: dialog.querySelector('.image-lightbox__close'),
      prev: dialog.querySelector('.image-lightbox__arrow--prev'),
      next: dialog.querySelector('.image-lightbox__arrow--next'),
      figure: dialog.querySelector('.image-lightbox__figure'),
      image: dialog.querySelector('.image-lightbox__image'),
      caption: dialog.querySelector('.image-lightbox__caption')
    };

    this.elements.close.addEventListener('click', () => this.close());
    this.elements.prev.addEventListener('click', () => this.showPrev());
    this.elements.next.addEventListener('click', () => this.showNext());

    this.elements.image.addEventListener('load', () => {
      this.dialog.classList.remove('image-lightbox--loading');
    });
    this.elements.image.addEventListener('error', () => {
      this.dialog.classList.remove('image-lightbox--loading');
    });

    dialog.addEventListener('click', this.handleBackdropClick);
    if (useNativeDialog) {
      dialog.addEventListener('cancel', this.handleCancel);
    }

    if (this.items.length < 2) {
      this.elements.prev.hidden = true;
      this.elements.next.hidden = true;
      this.elements.counter.hidden = true;
    }

    document.body.appendChild(dialog);
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                        */
  /* ------------------------------------------------------------------ */

  open(index, trigger) {
    if (!this.dialog) {
      this.buildDialog();
    }

    this.lastFocused = trigger || document.activeElement;
    this.isOpen = true;

    this.show(index);

    document.documentElement.classList.add(this.options.openHtmlClass);

    if (this.supportsDialog()) {
      if (!this.dialog.open) {
        this.dialog.showModal();
      }
    } else {
      this.dialog.hidden = false;
    }

    document.addEventListener('keydown', this.handleKeydown, true);

    this.elements.close.focus();
  }

  close() {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;

    document.removeEventListener('keydown', this.handleKeydown, true);
    document.documentElement.classList.remove(this.options.openHtmlClass);

    if (this.supportsDialog()) {
      if (this.dialog.open) {
        this.dialog.close();
      }
    } else {
      this.dialog.hidden = true;
    }

    if (typeof this.options.onClose === 'function') {
      this.options.onClose(this.currentIndex);
    }

    // Return focus to the trigger of the image that was viewed last so the
    // keyboard user continues exactly where they left off.
    const target = this.triggers[this.currentIndex] || this.lastFocused;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
    this.lastFocused = null;
  }

  /* ------------------------------------------------------------------ */
  /* Navigation                                                          */
  /* ------------------------------------------------------------------ */

  show(index) {
    const total = this.items.length;
    this.currentIndex = ((index % total) + total) % total;
    const item = this.items[this.currentIndex];

    this.dialog.classList.add('image-lightbox--loading');

    this.elements.image.alt = item.alt;
    if (item.srcset) {
      this.elements.image.setAttribute('srcset', item.srcset);
      this.elements.image.setAttribute('sizes', '100vw');
    } else {
      this.elements.image.removeAttribute('srcset');
      this.elements.image.removeAttribute('sizes');
    }
    this.elements.image.src = item.src;

    if (this.elements.image.complete && this.elements.image.naturalWidth > 0) {
      this.dialog.classList.remove('image-lightbox--loading');
    }

    if (item.caption) {
      this.elements.caption.textContent = item.caption;
      this.elements.caption.hidden = false;
    } else {
      this.elements.caption.textContent = '';
      this.elements.caption.hidden = true;
    }

    const counterText = this.options.labels.counter
        .replace('%current%', String(this.currentIndex + 1))
        .replace('%total%', String(total));

    this.elements.counter.textContent = counterText;
    this.elements.status.textContent = item.alt ? `${counterText}: ${item.alt}` : counterText;

    this.preloadNeighbours();
  }

  showPrev() {
    if (this.items.length < 2) {
      return;
    }
    this.show(this.currentIndex - 1);
  }

  showNext() {
    if (this.items.length < 2) {
      return;
    }
    this.show(this.currentIndex + 1);
  }

  preloadNeighbours() {
    if (this.items.length < 2) {
      return;
    }
    [this.currentIndex - 1, this.currentIndex + 1].forEach((neighbour) => {
      const total = this.items.length;
      const item = this.items[((neighbour % total) + total) % total];
      if (!item || !item.src) {
        return;
      }
      const preload = new Image();
      if (item.srcset) {
        preload.srcset = item.srcset;
        preload.sizes = '100vw';
      }
      preload.src = item.src;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Event handlers                                                      */
  /* ------------------------------------------------------------------ */

  handleKeydown(event) {
    if (!this.isOpen) {
      return;
    }

    switch (event.key) {
      case 'Escape':
      case 'Esc':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowLeft':
        event.preventDefault();
        this.showPrev();
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.showNext();
        break;

      case 'Tab':
        this.trapFocus(event);
        break;

      default:
        break;
    }
  }

  handleCancel(event) {
    // Native <dialog> Escape handling: route through close() so all cleanup runs.
    event.preventDefault();
    this.close();
  }

  handleBackdropClick(event) {
    // A click directly on the dialog element (outside the inner wrapper)
    // or on the inner wrapper's empty area closes the lightbox.
    if (event.target === this.dialog || event.target === this.elements.inside) {
      this.close();
    }
  }

  getFocusableElements() {
    const selector = 'button:not([disabled]):not([hidden]), [href], [tabindex]:not([tabindex="-1"])';
    return Array.from(this.dialog.querySelectorAll(selector))
        .filter((element) => element.offsetParent !== null || element === document.activeElement);
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (focusable.length < 1) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !this.dialog.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !this.dialog.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }
}
